import { Reducer } from "redux";
import tabsReducer, { TabsState } from "./tabsReducer";

interface State {
    token: string;
    logado: boolean;
    tenantId: string;
    expirado: boolean;
    administrador: boolean;
    usarSidebarLayout: boolean;
    isCadastroIncompleto: boolean;
    emContingencia: boolean;
    diasAtrasados: number;
    diasRestantesContingencia: number;
    bloqueadoPorInadimplencia: boolean;
    faturasEmAtraso: number;
    filialSelecionada: string | null;
    filialEstrita: boolean;
    filiais: Array<{ id: string; nome: string }>;
    empresaTemFiliais: boolean;
    tabs: TabsState;
}

const INITIAL_STATE: State = {
    token: '',
    tenantId: '',
    logado: false,
    expirado: false,
    administrador: false,
    usarSidebarLayout: false,
    isCadastroIncompleto: false,
    emContingencia: false,
    diasAtrasados: 0,
    diasRestantesContingencia: 0,
    bloqueadoPorInadimplencia: false,
    faturasEmAtraso: 0,
    filialSelecionada: null,
    filialEstrita: false,
    filiais: [],
    empresaTemFiliais: false,
    tabs: tabsReducer(undefined, { type: '@@INIT' }),
};

export const Types = {
    LOG_IN: 'LOG_IN',
    LOG_OUT: 'LOG_OUT',
    LOG_OUT_EXPIRED: 'LOG_OUT_EXPIRED',
    SET_CADASTRO_INCOMPLETO: 'SET_CADASTRO_INCOMPLETO',
    SET_CONTINGENCIA: 'SET_CONTINGENCIA',
    SET_BLOQUEADO_INADIMPLENCIA: 'SET_BLOQUEADO_INADIMPLENCIA',
    SET_FILIAL: 'SET_FILIAL',
    SET_FILIAL_ESTRITA: 'SET_FILIAL_ESTRITA',
    SET_FILIAIS: 'SET_FILIAIS',
};

export const Creators = {
    logIn: (token: string, tenantId: string, usarSidebarLayout: boolean) => ({
        type: Types.LOG_IN,
        token,
        tenantId,
        usarSidebarLayout,
    }),
    logOut: () => ({
        type: Types.LOG_OUT,
    }),
    logOutExpired: () => ({
        type: Types.LOG_OUT_EXPIRED,
    }),
    setCadastroIncompleto: (isIncomplete: boolean) => ({
        type: Types.SET_CADASTRO_INCOMPLETO,
        isIncomplete,
    }),
    setContingencia: (emContingencia: boolean, diasAtrasados: number, diasRestantesContingencia: number, faturasEmAtraso: number = 0) => ({
        type: Types.SET_CONTINGENCIA,
        emContingencia,
        diasAtrasados,
        diasRestantesContingencia,
        faturasEmAtraso,
    }),
    setBloqueadoInadimplencia: (bloqueado: boolean) => ({
        type: Types.SET_BLOQUEADO_INADIMPLENCIA,
        bloqueado,
    }),
};

const clearNotificationStorage = () => {
    localStorage.removeItem('clearedNotifications');
    localStorage.removeItem('markedAsRead');
};

const reducer: Reducer<State> = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case Types.LOG_IN:
            clearNotificationStorage();
            return {
                ...state,
                token: action.token,
                logado: true,
                expirado: false,
                usarSidebarLayout: action.usarSidebarLayout,
                tenantId: action.tenantId,
                // Limpa qualquer estado de filial persistido de uma sessão anterior
                // (navegador compartilhado): evita que um usuário sem filial herde
                // a filial selecionada de outro e receba dados filtrados/headers de filial.
                filialSelecionada: null,
                filialEstrita: false,
                filiais: [],
                empresaTemFiliais: false,
            };
        case Types.LOG_OUT:
            return {
                ...state,
                token: '',
                logado: false,
                expirado: false,
                usarSidebarLayout: false,
                isCadastroIncompleto: false,
                emContingencia: false,
                diasAtrasados: 0,
                diasRestantesContingencia: 0,
                bloqueadoPorInadimplencia: false,
                faturasEmAtraso: 0,
                filialSelecionada: null,
                filialEstrita: false,
                filiais: [],
                empresaTemFiliais: false,
                tabs: tabsReducer(undefined, { type: '@@INIT' }),
            };
        case Types.LOG_OUT_EXPIRED:
            return {
                ...state,
                token: '',
                logado: false,
                expirado: true,
                usarSidebarLayout: false,
                isCadastroIncompleto: false,
                emContingencia: false,
                diasAtrasados: 0,
                diasRestantesContingencia: 0,
                bloqueadoPorInadimplencia: false,
                faturasEmAtraso: 0,
                filialSelecionada: null,
                filialEstrita: false,
                filiais: [],
                empresaTemFiliais: false,
                tabs: tabsReducer(undefined, { type: '@@INIT' }),
            };
        case Types.SET_CADASTRO_INCOMPLETO:
            return {
                ...state,
                isCadastroIncompleto: action.isIncomplete,
            };
        case Types.SET_CONTINGENCIA:
            return {
                ...state,
                emContingencia: action.emContingencia,
                diasAtrasados: action.diasAtrasados,
                diasRestantesContingencia: action.diasRestantesContingencia,
                faturasEmAtraso: action.faturasEmAtraso ?? 0,
            };
        case Types.SET_BLOQUEADO_INADIMPLENCIA:
            return {
                ...state,
                bloqueadoPorInadimplencia: action.bloqueado,
            };
        case Types.SET_FILIAL:
            return {
                ...state,
                filialSelecionada: action.filialId,
            };
        case Types.SET_FILIAL_ESTRITA:
            return {
                ...state,
                filialEstrita: action.filialEstrita,
            };
        case Types.SET_FILIAIS:
            return {
                ...state,
                filiais: action.filiais,
                empresaTemFiliais: action.filiais.length > 0,
            };
        default: {
            if (action.type && typeof action.type === 'string' && action.type.startsWith('TABS/')) {
                const newTabs = tabsReducer(state.tabs, action);
                if (newTabs !== state.tabs) {
                    return { ...state, tabs: newTabs };
                }
            }
            return state;
        }
    }
};

export default reducer;